import React, { useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import { useNavigate } from "react-router";
import toast from "react-hot-toast";
import sslLogo from "../../../assets/paymentMethod/pgwlogo.png";
import banglaqrLogo from "../../../assets/paymentMethod/banglaqr.png";
import QR from "../../../assets/paymentMethod/qr.jpeg";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { RiSecurePaymentFill } from "react-icons/ri";

// Backend base URL (change to your production API when you deploy)
const API_BASE = "https://carola-occasional-rhona.ngrok-free.dev";
const PAY_SSL = 1;
const PAY_BANGLA_QR = 2;

const PayNow = ({ open, setOpen, invoiceId, amountDue, onPaid }) => {
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const closeModal = () => {
    if (isLoading) return;
    setOpen(false);
    setShowQR(false);
    setTransactionId("");
  };

  const callPay = async (body) => {
    const res = await fetch(`${API_BASE}/paylater/pay`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Payment failed");
    return data;
  };

  const handlePayNow = async () => {
    if (!invoiceId) return toast.error("Invoice not found");

    if (paymentMethod === 0) {
      return toast.error("Please select a payment method");
    }

    if (paymentMethod === PAY_BANGLA_QR) {
      setTransactionId("");
      setShowQR(true);
      return;
    }

    try {
      setIsLoading(true);
      const data = await callPay({ invoiceId, paymentMethod: PAY_SSL });
      if (!data.GatewayPageURL) throw new Error("Gateway URL not received");
      window.location.href = data.GatewayPageURL;
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message || "Payment failed. Please try again.");
    }
  };

  const handleBanglaQR = async () => {
    const trx = transactionId.trim();
    if (!trx) return toast.error("Transaction ID Required");

    try {
      setIsLoading(true);
      await callPay({
        invoiceId,
        paymentMethod: PAY_BANGLA_QR,
        transactionId: trx,
      });
      setIsLoading(false);
      toast.success("Payment received. Booking confirmed!");
      if (onPaid) onPaid();
      setOpen(false);
      navigate(`/hotel/payment/success/${trx}`);
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message || "Could not record payment");
    }
  };

  const MethodCard = ({ value, logo, label }) => {
    const selected = paymentMethod === value;
    return (
      <div className="flex flex-col gap-y-1">
        <div
          onClick={() => setPaymentMethod(selected ? 0 : value)}
          className={`relative w-28 lg:w-40 h-28 lg:h-40 border ${
            selected ? "border-green-500" : ""
          } rounded-lg p-2 cursor-pointer`}
        >
          <img
            className="w-full h-full object-contain"
            src={logo}
            alt={label}
          />
          {selected && (
            <div className="absolute -top-2 -right-3 bg-white rounded-full">
              <IoIosCheckmarkCircle className="text-2xl text-green-700" />
            </div>
          )}
        </div>
        <div className="flex justify-start items-center gap-1">
          <RiSecurePaymentFill className="text-sm lg:text-md text-blue-700" />
          <p className="text-[10px] lg:text-xs">{label}</p>
        </div>
      </div>
    );
  };

  return (
    <Modal
      aria-labelledby="modal-title"
      aria-describedby="modal-desc"
      open={open}
      onClose={closeModal}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: { xs: 320, sm: 400, md: 500 },
          borderRadius: "md",
          p: 3,
          boxShadow: "lg",
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />
        <p className="font-bold uppercase">Payment Method</p>
        {invoiceId && (
          <p className="text-[10px] lg:text-xs text-gray-500 mt-1">
            Invoice: {invoiceId}
          </p>
        )}

        {showQR ? (
          /* ---------------- Bangla QR screen ---------------- */
          <div className="space-y-3 mt-3">
            <div>
              <img
                className="w-full h-60 object-contain"
                src={QR}
                alt="Bangla QR"
              />
            </div>

            <div>
              <label className="text-xs lg:text-sm" htmlFor="transactionId">
                Transaction Id <span className="text-red-500">*</span>
              </label>
              <input
                id="transactionId"
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                className="p-1 lg:p-2 rounded-md border w-full focus:outline-none"
                placeholder="Transaction ID"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs lg:text-sm font-medium uppercase">
                Instructions:
              </p>
              <p className="text-[9px] lg:text-[10px]">
                1. Scan with any payment app including MFS (Mobile Financial
                Service) and Banking app.
              </p>
              <p className="text-[9px] lg:text-[10px]">
                2. If your payment is successful, add your transaction ID in the
                input box above.
              </p>
              <p className="text-[9px] lg:text-[10px]">
                3. Then submit to checkout. If anything happens, please contact
                our support for help.
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleBanglaQR}
                className="bg-[#161f52] w-full hover:bg-[#0a0e25] duration-300 text-white font-medium py-2 px-4 rounded disabled:opacity-60"
                disabled={isLoading}
              >
                {isLoading ? "Processing, wait!" : "Check out now!"}
              </button>
              <button
                onClick={() => setShowQR(false)}
                className="w-full text-xs text-gray-600 hover:underline"
                disabled={isLoading}
              >
                Back to payment methods
              </button>
            </div>
          </div>
        ) : (
          /* ---------------- Method selection screen ---------------- */
          <div className="space-y-5 mt-5">
            <div className="flex justify-center items-center gap-5 lg:gap-10 p-2">
              <MethodCard
                value={PAY_SSL}
                logo={sslLogo}
                label="Use SSL Commerz"
              />
              <MethodCard
                value={PAY_BANGLA_QR}
                logo={banglaqrLogo}
                label="Use Bangla QR"
              />
            </div>

            <div>
              <button
                className="bg-[#161f52] w-full hover:bg-[#0a0e25] duration-300 text-white font-bold py-2 px-4 rounded disabled:opacity-60"
                onClick={handlePayNow}
                disabled={isLoading}
              >
                {isLoading
                  ? "Processing, wait!"
                  : `Pay Now ${amountDue ?? ""} BDT`}
              </button>
            </div>
          </div>
        )}
      </Sheet>
    </Modal>
  );
};

export default PayNow;
